/**
 * 性能监控工具
 * 监控 Web Vitals 和自定义性能指标
 */

interface Metric {
  name: string;
  value: number;
  id: string;
  label?: 'web-vital' | 'custom';
}

export class PerformanceMonitor {
  private static metrics: Metric[] = [];
  private static isInitialized = false;
  
  /**
   * 初始化性能监控
   */
  static init() {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }
    
    this.isInitialized = true;
    
    // 监控 Web Vitals
    if ('web-vital' in window || typeof window !== 'undefined') {
      this.initWebVitals();
    }
    
    // 监控自定义指标
    this.measureAppReady();
    this.measureFirstInteraction();
    
    // 监控资源加载
    this.observeResourceTiming();
  }
  
  /**
   * 初始化 Web Vitals 监控
   */
  private static async initWebVitals() {
    try {
      const vitals = await import('web-vitals');
      
      vitals.onCLS(metric => this.sendMetric({ ...metric, label: 'web-vital' }));
      vitals.onFCP(metric => this.sendMetric({ ...metric, label: 'web-vital' }));
      vitals.onLCP(metric => this.sendMetric({ ...metric, label: 'web-vital' }));
      vitals.onTTFB(metric => this.sendMetric({ ...metric, label: 'web-vital' }));
      vitals.onINP(metric => this.sendMetric({ ...metric, label: 'web-vital' }));
    } catch (error) {
      console.error('Failed to load web-vitals:', error);
    }
  }
  
  /**
   * 测量应用准备时间
   */
  private static measureAppReady() {
    const appReadyTime = performance.now();
    this.sendMetric({
      name: 'app-ready',
      value: appReadyTime,
      id: `app-ready-${Date.now()}`,
      label: 'custom'
    });
  }
  
  /**
   * 测量首次交互时间
   */
  private static measureFirstInteraction() {
    let firstInteractionRecorded = false;
    
    const recordFirstInteraction = () => {
      if (firstInteractionRecorded) return;
      
      firstInteractionRecorded = true;
      const interactionTime = performance.now();
      
      this.sendMetric({
        name: 'first-interaction',
        value: interactionTime,
        id: `first-interaction-${Date.now()}`,
        label: 'custom'
      });
      
      // 移除监听器
      ['click', 'touchstart', 'keydown'].forEach(event => {
        document.removeEventListener(event, recordFirstInteraction);
      });
    };
    
    // 监听用户交互
    ['click', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, recordFirstInteraction, { once: true });
    });
  }
  
  /**
   * 监控资源加载性能
   */
  private static observeResourceTiming() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'resource') {
            const resourceEntry = entry as PerformanceResourceTiming;
            
            // 只监控关键资源
            if (this.isKeyResource(resourceEntry.name)) {
              this.sendMetric({
                name: `resource-${this.getResourceType(resourceEntry.name)}`,
                value: resourceEntry.duration,
                id: `resource-${Date.now()}-${Math.random()}`,
                label: 'custom'
              });
            }
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
    }
  }
  
  /**
   * 判断是否为关键资源
   */
  private static isKeyResource(url: string): boolean {
    return url.includes('/_next/') || 
           url.includes('/api/') || 
           url.endsWith('.css') || 
           url.endsWith('.js');
  }
  
  /**
   * 获取资源类型
   */
  private static getResourceType(url: string): string {
    if (url.endsWith('.css')) return 'css';
    if (url.endsWith('.js')) return 'js';
    if (url.includes('/api/')) return 'api';
    if (url.includes('/_next/')) return 'next';
    return 'other';
  }
  
  /**
   * 发送性能指标
   */
  private static sendMetric(metric: Metric) {
    // 存储指标
    this.metrics.push(metric);
    
    // 在开发环境打印
    if (process.env.NODE_ENV === 'development') {
      // console.log(`[Performance] ${metric.name}:`, metric.value.toFixed(2), 'ms');
    }
    
    // 在生产环境可以发送到分析服务
    if (process.env.NODE_ENV === 'production') {
      // 批量发送以减少请求
      this.batchSendMetrics();
    }
  }
  
  /**
   * 批量发送指标
   */
  private static batchSendMetrics() {
    if (this.metrics.length === 0) return;
    
    // 这里可以实现发送到分析服务的逻辑
    // 例如: Google Analytics, Sentry, 或自定义分析服务
    
    // 清空已发送的指标
    this.metrics = [];
  }
  
  /**
   * 获取当前收集的指标
   */
  static getMetrics(): Metric[] {
    return [...this.metrics];
  }
  
  /**
   * 手动记录自定义指标
   */
  static measure(name: string, value: number) {
    this.sendMetric({
      name,
      value,
      id: `${name}-${Date.now()}`,
      label: 'custom'
    });
  }
  
  /**
   * 性能标记
   */
  static mark(name: string) {
    if ('performance' in window && 'mark' in performance) {
      performance.mark(name);
    }
  }
  
  /**
   * 性能测量
   */
  static measureBetween(startMark: string, endMark: string, measureName: string) {
    if ('performance' in window && 'measure' in performance) {
      try {
        performance.measure(measureName, startMark, endMark);
        const measure = performance.getEntriesByName(measureName)[0];
        if (measure) {
          this.measure(measureName, measure.duration);
        }
      } catch (error) {
        console.error('Failed to measure performance:', error);
      }
    }
  }
}